import React, { useState, useRef, useEffect, useMemo } from 'react';
import moment from 'moment';
import 'moment/locale/fr';

const DateTimePicker = ({
  value,
  onChange,
  isDate = true,
  format,
  locale = 'en',
  maxView = "year",
  minView = "month",
  showHeader = true,
  className = "",
  momentDate
}) => {
  useEffect(() => {
    const loadLocale = async () => {
      try {
        const baseLocale = locale.split('_')[0];
        await import(`moment/locale/${baseLocale}`);
        moment.locale(baseLocale);
      } catch (error) {
        console.warn(`Failed to load locale ${locale}, falling back to 'en'`, error);
        moment.locale('en');
      }
    };

    loadLocale();
  }, [locale]);



  const initialDate = moment(value, format).isValid()
    ? moment(value, format)
    : moment();

  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(initialDate);
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (moment(value, format).isValid()) {
      setCurrentDate(moment(value, format));
    }
  }, [value, format]);

  const displayValue = useMemo(() => {
    if (!value || !moment(value, format).isValid()) {
      return isDate
        ? moment().format(format)
        : moment().format('HH:00');
    }
    return moment(value, format).format(format);
  }, [value, format, isDate]);

  const isSelectableDate = (date) => {
    const today = moment().startOf('day');
    const dateToCheck = date.clone().startOf('day');
    return dateToCheck.isSameOrAfter(today);
  };

  const renderDatePicker = () => {
    const startOfMonth = currentDate.clone().startOf('month');
    const endOfMonth = currentDate.clone().endOf('month');
    const days = [];
    let week = [];

    const firstDay = startOfMonth.clone();
    const startDay = firstDay.clone().startOf('week');
    while (startDay.isBefore(firstDay)) {
      week.push(startDay.clone());
      startDay.add(1, 'day');
    }

    let day = startOfMonth.clone();
    while (day.isSameOrBefore(endOfMonth)) {
      if (week.length === 7) {
        days.push(week);
        week = [];
      }
      week.push(day.clone());
      day.add(1, 'day');
    }

    while (week.length < 7) {
      week.push(day.clone());
      day.add(1, 'day');
    }
    days.push(week);

    return (
      <div className="moment-picker-container">
        {showHeader && (
          <table className="header-view">
            <thead>
              <tr>
                <th
                  className={!currentDate.clone().subtract(1, 'month').isBefore(moment(), 'month') ? '' : 'disabled'}
                  onClick={() => !currentDate.clone().subtract(1, 'month').isBefore(moment(), 'month') &&
                    setCurrentDate(currentDate.clone().subtract(1, 'month'))}
                >
                  ←
                </th>
                <th>{currentDate.format('MMMM YYYY')}</th>
                <th onClick={() => setCurrentDate(currentDate.clone().add(1, 'month'))}>→</th>
              </tr>
            </thead>
          </table>
        )}
        <table className="moment-picker-calendar">
          <thead>
            <tr>
              {moment.weekdaysShort(true).map(day => (
                <th key={day}>{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {days.map((week, i) => (
              <tr key={i}>
                {week.map(day => {
                  const isDisabled = !isSelectableDate(day);
                  const isOtherMonth = !day.isSame(currentDate, 'month');
                  return (
                    <td
                      key={day.format('D')}
                      className={`
                      ${isOtherMonth ? 'other-month' : ''}
                      ${isDisabled ? 'disabled' : ''}
                      ${day.isSame(moment(value, format), 'day') ? 'selected' : ''}
                    `}
                      onClick={() => {
                        if (!isDisabled && !isOtherMonth) {
                          onChange(day.format('DD/MM/YYYY'));
                          setIsOpen(false);
                        }
                      }}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && !isDisabled && !isOtherMonth) {
                          e.preventDefault();
                          onChange(day.format('DD/MM/YYYY'));
                          setIsOpen(false);
                        }
                      }}
                      tabIndex={!isDisabled && !isOtherMonth ? "0" : "-1"}
                      role="gridcell"
                      aria-selected={day.isSame(moment(value, format), 'day')}
                    >
                      {day.format('D')}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  const renderTimePicker = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const rows = [];
    for (let i = 0; i < hours.length; i += 4) {
      rows.push(hours.slice(i, i + 4));
    }

    const isSelectableTime = (hour) => {
      if (!momentDate) return true;
      const today = moment();
      const selectedDate = moment(value, format);
      return !selectedDate.isSame(today, 'day') || hour > today.hour();
    };

    return (
      <div className="moment-picker-container">
        <table className="moment-picker-hours">
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map(hour => {
                  const timeString = `${hour.toString().padStart(2, '0')}:00`;
                  const isDisabled = !isSelectableTime(hour);
                  return (
                    <td
                      key={hour}
                      className={`
    ${isDisabled ? 'disabled' : ''}
    ${value === timeString ? 'selected' : ''}
  `}
                      onClick={() => {
                        if (!isDisabled) {
                          onChange(timeString);
                          setIsOpen(false);
                        }
                      }}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
                          e.preventDefault();
                          onChange(timeString);
                          setIsOpen(false);
                        }
                      }}
                      tabIndex={!isDisabled ? "0" : "-1"}
                      role="gridcell"
                      aria-selected={value === timeString}
                    >
                      {timeString}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="moment-picker" ref={pickerRef}>
      <span
        className={`datetimepicker ${isDate ? 'dateinput' : 'timeinput'} mainColor ${className}`}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        tabIndex="0"
        role="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={isDate ? "Sélectionner une date" : "Sélectionner une heure"}
      >
        {displayValue}
      </span>
      {isOpen && (isDate ? renderDatePicker() : renderTimePicker())}
    </div>
  );
};

export default DateTimePicker;